import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import Loader from 'components/Loader/Loader'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import MissionStats from 'components/MissionStats/MissionStats'
import RobotTypeLabel from 'components/RobotTypeLabel/RobotTypeLabel'
import Title from 'components/Title/Title'
import { Feature } from 'geojson'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { ReactComponent as HeadlightOffSvg } from 'images/icons/headlight_off.svg'
import { ReactComponent as HeadlightOnSvg } from 'images/icons/headlight_on.svg'
import { ReactComponent as ParkingSvg } from 'images/icons/parking.svg'
import { ReactComponent as ChargingSvg } from 'images/icons/plug_in.svg'
import { ReactComponent as WashingSvg } from 'images/icons/shower.svg'
import CleaningPointsOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningPointsOnMap'
import CleaningRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningRoutesOnMap'
import CleaningZonesNamesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesNamesOnMap'
import CleaningZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesOnMap'
import MissionStartFinishPoints from 'pages/Mission/MissionView/MissionMap/Layers/MissionStartFinishPoints'
import RestrictZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/RestrictZonesOnMap'
import TransferRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/TransferRoutesOnMap'
import SelectMissionModal from 'pages/Robot/RobotView/RobotLeftBlock/RobotDetails/RobotMissionMainStatus/SelectMissionModal/SelectMissionModal'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapboxMap } from 'react-map-gl'
import { useNavigate } from 'react-router-dom'
import { PATH, PROGRESS_TYPES } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { IMapboxElement, IMission, IMissionInfo } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './ScenarioView.module.css'
import notCalculatedStyle from 'styles/missionRowNotCalculated.module.css'
import pageLayoutStyle from 'styles/pageLayout.module.css'

interface ScenarioData {
  id: number
  name: string
  description?: string
  missions_ids: number[]
  missions_locations?: (string | null)[]
  mission_equipment_setups?: Record<string, { front_lights?: boolean }>
  robot_type?: string
  missions_type_inconsistent?: boolean
  overall_execution_time?: number
  overall_length?: number
  overall_cleaned_area?: number
  maintenance?: {
    parking?: boolean
    charging?: boolean
    washing?: boolean
  }
}

const ScenarioItem = ({
  mission,
  onClick,
  highlighted,
  onExclude,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  location,
  checked,
  onCheckboxChange,
  typeMismatch,
  equipmentSetup,
  onEquipmentSetupChange,
  index,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging,
}: {
  mission: IMissionInfo
  onClick: () => void
  highlighted?: boolean
  onExclude: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  isFirst: boolean
  isLast: boolean
  location?: string | null
  showCheckbox?: boolean
  checked?: boolean
  onCheckboxChange?: (checked: boolean) => void
  /** Red row when mission type does not match scenario type (mixed types) */
  typeMismatch?: boolean
  equipmentSetup?: { front_lights?: boolean }
  onEquipmentSetupChange?: (setup: { front_lights: boolean }) => void
  index: number
  onDragStart: (e: React.DragEvent<HTMLDivElement>, index: number) => void
  onDragOver: (e: React.DragEvent<HTMLDivElement>, index: number) => void
  onDrop: (e: React.DragEvent<HTMLDivElement>, index: number) => void
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void
  isDragging?: boolean
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const goToMission = () => {
    navigate(`${PATH.MISSION}${mission.id}`)
  }

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, index)}
      onDragOver={e => onDragOver(e, index)}
      onDrop={e => onDrop(e, index)}
      onDragEnd={onDragEnd}
      className={clsx(style.missionRow, {
        [style.highlighted]: highlighted,
        [notCalculatedStyle.missionRowNotCalculated]: mission.status !== 'ok' || typeMismatch,
        [style.isDragging]: isDragging,
      })}
      onClick={onClick}
    >
      <div className={style.headerColCentered} style={{ cursor: 'grab' }}>
        <span></span>⠿
      </div>
      <div className={style.headerCol}>{mission.id}</div>
      <div className={style.colLeftMultiline}>{mission.name}</div>
      <div className={style.headerColCentered}>{location === null || location === undefined ? 'GNSS' : 'SLAM'}</div>
      <div className={style.headerColCentered} onClick={e => e.stopPropagation()}>
        <div
          onClick={() => onEquipmentSetupChange?.({ front_lights: !equipmentSetup?.front_lights })}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {equipmentSetup?.front_lights ? (
            <HeadlightOnSvg width={24} height={24} />
          ) : (
            <HeadlightOffSvg width={24} height={24} />
          )}
        </div>
      </div>
      <div className={style.headerColCentered} onClick={e => e.stopPropagation()}>
        <input type="checkbox" checked={checked} onChange={e => onCheckboxChange?.(e.target.checked)} />
      </div>
      <div className={style.headerCol} onClick={e => e.stopPropagation()}>
        <ActionsMenu>
          {!isFirst && (
            <ActionsMenuItem onClick={onMoveUp} data-test-id={getTestId('move-up-mission')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Move Up</span>
            </ActionsMenuItem>
          )}
          {!isLast && (
            <ActionsMenuItem onClick={onMoveDown} data-test-id={getTestId('move-down-mission')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Move Down</span>
            </ActionsMenuItem>
          )}
          <ActionsMenuItem onClick={goToMission} data-test-id={getTestId('edit-mission')}>
            <span className={style.menuItemIcon} />
            <span className={style.menuItemText}>{t('common.edit')}</span>
          </ActionsMenuItem>
          <ActionsMenuItem onClick={onExclude} data-test-id={getTestId('exclude-mission')}>
            <RemoveSvg className={style.menuItemIcon} />
            <span>{t('common.exclude')}</span>
          </ActionsMenuItem>
        </ActionsMenu>
      </div>
    </div>
  )
}

const MaintenanceItem = ({
  type,
  icon: Icon,
}: {
  type: string
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string | undefined }>
}) => {
  return (
    <div className={style.missionRow} style={{ cursor: 'default' }}>
      <div className={style.headerColCentered}></div>
      <div className={style.headerCol}>
        <Icon width={22} height={22} />
      </div>
      <div className={style.headerCol}>{type}</div>
      <div className={style.headerColCentered}></div>
      <div className={style.headerColCentered}></div>
      <div className={style.headerColCentered}></div>
      <div className={style.headerCol}></div>
    </div>
  )
}

// Global promise cache to deduplicate fetches across components and remounts
const missionPromises: Record<number, Promise<IMission>> = {}

const ScenarioView = ({
  scenario,
  onUpdated,
}: {
  scenario: ScenarioData
  onUpdated: (background?: boolean) => void
}) => {
  const { t } = useTranslation()
  const [highlightedMissionId, setHighlightedMissionId] = useState<number | undefined>(undefined)
  const [selectedMissionIndices, setSelectedMissionIndices] = useState<Set<number>>(new Set())
  const hasInitializedSelection = useRef(false)
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)

  // Add mission dialog
  const [addMissionDialog, setAddMissionDialog] = useState(false)

  // Mission list state
  const [missions, setMissions] = useState<IMissionInfo[]>([])
  const [missionsProgress, setMissionsProgress] = useState(PROGRESS_TYPES.IDLE)

  // Selected mission details for the map
  const [selectedMissionDetails, setSelectedMissionDetails] = useState<IMission | undefined>(undefined)

  const missionCacheRef = useRef<Record<number, IMission>>({})
  const [cacheVersion, setCacheVersion] = useState(0)

  const missionIdsKey = scenario.missions_ids.join(',')

  const individualMissionDetails = useMemo(() => {
    const selectedIds = Array.from(selectedMissionIndices).map(idx => scenario.missions_ids[idx])
    return selectedIds.map(id => missionCacheRef.current[id]).filter(Boolean) as IMission[]
  }, [selectedMissionIndices, missionIdsKey, cacheVersion])

  const onMapInited = () => {
    const m = mapRef.current?.getMap()
    if (m) setMap(m)
  }

  // Fetch mission info for each mission_id in the scenario
  const fetchMissions = useCallback(
    async (background: boolean = false) => {
      if (!scenario.missions_ids || scenario.missions_ids.length === 0) {
        setMissions([])
        setMissionsProgress(PROGRESS_TYPES.SUCCESS)
        return
      }
      try {
        if (!background) {
          setMissionsProgress(PROGRESS_TYPES.WORK)
        }

        // Fetch only the missions that belong to the scenario.
        const promises = scenario.missions_ids.map(id => {
          if (!missionPromises[id]) {
            missionPromises[id] = MainApi.fetchMission(id).finally(() => {
              // Delay deletion slightly to catch any very close overlapping calls
              setTimeout(() => {
                delete missionPromises[id]
              }, 100)
            })
          }
          return missionPromises[id]
        })
        const results = await Promise.all(promises)

        setMissions(results.map(r => r.mission))

        // Populate cache so useMemo-based values update
        results.forEach(m => {
          missionCacheRef.current[m.mission.id] = m
        })
        setCacheVersion(v => v + 1)

        setMissionsProgress(PROGRESS_TYPES.SUCCESS)
      } catch (error) {
        setMissionsProgress(PROGRESS_TYPES.ERROR)
        console.warn('[ScenarioView] fetchMissions error:', error)
      }
    },
    [missionIdsKey]
  )

  useEffect(() => {
    fetchMissions(missions.length > 0)
  }, [fetchMissions])

  const prevMissionsLength = useRef(scenario.missions_ids.length)
  // Initialize and sync selected mission indices
  useEffect(() => {
    if (scenario.missions_ids.length > 0) {
      if (!hasInitializedSelection.current || prevMissionsLength.current === 0) {
        setSelectedMissionIndices(new Set(scenario.missions_ids.map((_, i) => i)))
        hasInitializedSelection.current = true
      } else if (scenario.missions_ids.length > prevMissionsLength.current) {
        // When missions are added, we want them to be checked by default
        setSelectedMissionIndices(prev => {
          const next = new Set(prev)
          for (let i = prevMissionsLength.current; i < scenario.missions_ids.length; i++) {
            next.add(i)
          }
          return next
        })
      }
    } else {
      setSelectedMissionIndices(new Set())
      hasInitializedSelection.current = false
    }
    prevMissionsLength.current = scenario.missions_ids.length
  }, [missionIdsKey])

  // Fetch individual mission details for checkboxes
  useEffect(() => {
    const fetchIndividual = async () => {
      if (selectedMissionIndices.size > 0) {
        try {
          const selectedIds = Array.from(selectedMissionIndices).map(idx => scenario.missions_ids[idx])
          const uniqueIds = Array.from(new Set(selectedIds))

          // Use the common promise cache
          const promises = uniqueIds.map(id => {
            if (!missionPromises[id]) {
              missionPromises[id] = MainApi.fetchMission(id).finally(() => {
                // Delay deletion slightly to catch any very close overlapping calls
                setTimeout(() => {
                  delete missionPromises[id]
                }, 100)
              })
            }
            return missionPromises[id]
          })

          const results = await Promise.all(promises)

          let updated = false
          results.forEach(m => {
            if (!missionCacheRef.current[m.mission.id]) {
              missionCacheRef.current[m.mission.id] = m
              updated = true
            }
          })
          if (updated) {
            setCacheVersion(v => v + 1)
          }
        } catch (error) {
          console.error('Failed to fetch individual mission details', error)
        }
      }
    }
    fetchIndividual()
  }, [selectedMissionIndices, missionIdsKey])

  // Fetch full mission details when a mission is selected
  useEffect(() => {
    const fetchMissionDetails = async () => {
      if (highlightedMissionId) {
        const cached = missionCacheRef.current[highlightedMissionId]
        if (cached) {
          setSelectedMissionDetails(cached)
          return
        }

        try {
          const missionData = await MainApi.fetchMission(highlightedMissionId)
          missionCacheRef.current[missionData.mission.id] = missionData
          setCacheVersion(v => v + 1)
          setSelectedMissionDetails(missionData)
        } catch (error) {
          console.error('Failed to fetch selected mission', error)
        }
      } else {
        setSelectedMissionDetails(undefined)
      }
    }
    fetchMissionDetails()
  }, [highlightedMissionId])

  // Zoom map to selected mission
  useEffect(() => {
    if (map && selectedMissionDetails) {
      const features: Feature[] = [
        ...selectedMissionDetails.cleaningZones,
        ...selectedMissionDetails.restrictZones,
        ...selectedMissionDetails.cleaningRoutes,
        ...selectedMissionDetails.transferRoutes,
        ...(selectedMissionDetails.pointRoutes || []),
      ]
      if (features.length > 0) {
        zoomMapTo(map, features)
      }
    }
  }, [map, selectedMissionDetails])

  // Zoom map to all missions when details are first loaded
  const hasZoomedAll = useRef(false)
  useEffect(() => {
    if (map && individualMissionDetails.length > 0 && !hasZoomedAll.current) {
      const features: Feature[] = individualMissionDetails.flatMap(m => [
        ...m.cleaningZones,
        ...m.restrictZones,
        ...m.cleaningRoutes,
        ...m.transferRoutes,
        ...(m.pointRoutes || []),
      ])
      if (features.length > 0) {
        zoomMapTo(map, features)
        hasZoomedAll.current = true
      }
    }
  }, [map, individualMissionDetails])

  const toggleShowAll = async () => {
    if (selectedMissionIndices.size === missions.length) {
      setSelectedMissionIndices(new Set())
    } else {
      setSelectedMissionIndices(new Set(missions.map((_, i) => i)))
    }
  }

  const areAllLightsOn = useMemo(() => {
    if (missions.length === 0) return false
    return missions.every((_, index) => scenario.mission_equipment_setups?.[index]?.front_lights === true)
  }, [missions, scenario.mission_equipment_setups])

  const toggleAllEquipmentSetup = async (on: boolean) => {
    try {
      const updatedSetups: Record<string, { front_lights?: boolean }> = { ...(scenario.mission_equipment_setups || {}) }
      missions.forEach((_, index) => {
        updatedSetups[index] = { ...updatedSetups[index], front_lights: on }
      })

      const patchData: any = { mission_equipment_setups: updatedSetups }
      await MainApi.patchScenarios(scenario.id, patchData)
      onUpdated(true)
    } catch (error) {
      console.error('Error toggling all equipment setups:', error)
    }
  }

  const addMission = async (mission: IMissionInfo | undefined) => {
    if (!mission) return
    try {
      const isFirst = scenario.missions_ids.length === 0
      const updatedIds = [...scenario.missions_ids, mission.id]
      const patchData: any = { missions_ids: updatedIds }
      if (isFirst && mission.robot_type) {
        patchData.robot_type = mission.robot_type
      }
      await MainApi.patchScenarios(scenario.id, patchData)
      setAddMissionDialog(false)
      onUpdated(true)
    } catch (error) {
      console.error('Error adding mission:', error)
    }
  }

  const excludeMission = async (index: number) => {
    try {
      const updatedIds = scenario.missions_ids.filter((_, i) => i !== index)
      const patchData: any = { missions_ids: updatedIds }

      // Optimistic UI updates
      setMissions(prev => prev.filter((_, i) => i !== index))
      setSelectedMissionIndices(prev => {
        const next = new Set<number>()
        prev.forEach(i => {
          if (i < index) next.add(i)
          else if (i > index) next.add(i - 1)
        })
        return next
      })

      await MainApi.patchScenarios(scenario.id, patchData)
      onUpdated(true)
    } catch (error) {
      console.error('Error excluding mission:', error)
      fetchMissions(true)
    }
  }

  const moveMission = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= scenario.missions_ids.length) return
    const updatedIds = [...scenario.missions_ids]
    ;[updatedIds[index], updatedIds[newIndex]] = [updatedIds[newIndex], updatedIds[index]]

    // Optimistic UI updates
    const updatedMissions = [...missions]
    ;[updatedMissions[index], updatedMissions[newIndex]] = [updatedMissions[newIndex], updatedMissions[index]]
    setMissions(updatedMissions)

    // Remap checkboxes
    setSelectedMissionIndices(prev => {
      const next = new Set<number>()
      prev.forEach(i => {
        if (i === index) next.add(newIndex)
        else if (i === newIndex) next.add(index)
        else next.add(i)
      })
      return next
    })

    // Remap equipment setups dictionary locally
    const currentSetups = scenario.mission_equipment_setups || {}
    const newSetups: Record<string, { front_lights?: boolean }> = {}
    const numMissions = updatedIds.length
    for (let i = 0; i < numMissions; i++) {
      let oldIdx = i
      if (i === index) oldIdx = newIndex
      else if (i === newIndex) oldIdx = index

      if (currentSetups[oldIdx]) {
        newSetups[i] = currentSetups[oldIdx]
      }
    }

    try {
      await MainApi.patchScenarios(scenario.id, {
        missions_ids: updatedIds,
        mission_equipment_setups: newSetups,
      } as any)
      onUpdated(true)
    } catch (error) {
      console.error('Error moving mission:', error)
      // Revert optimistic update on error
      fetchMissions(true)
    }
  }

  const toggleEquipmentSetup = async (index: number) => {
    try {
      const isCurrentlyOn = scenario.mission_equipment_setups?.[index]?.front_lights ?? false
      const updatedSetups = { ...(scenario.mission_equipment_setups || {}) }

      updatedSetups[index] = { front_lights: !isCurrentlyOn }

      const patchData: any = { mission_equipment_setups: updatedSetups }
      await MainApi.patchScenarios(scenario.id, patchData)
      onUpdated(true)
    } catch (error) {
      console.error('Error toggling equipment setup:', error)
    }
  }

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString())
    e.dataTransfer.effectAllowed = 'move'
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragOverIndex !== index) {
      setDragOverIndex(index)
    }
  }

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault()
    const startIndexStr = e.dataTransfer.getData('text/plain')
    if (!startIndexStr) return
    const startIndex = parseInt(startIndexStr, 10)

    if (startIndex === dropIndex) {
      setDraggedIndex(null)
      setDragOverIndex(null)
      return
    }

    const updatedIds = [...scenario.missions_ids]
    const [movedId] = updatedIds.splice(startIndex, 1)
    updatedIds.splice(dropIndex, 0, movedId)

    // Apply optimistic updates
    const updatedMissions = [...missions]
    const [movedMission] = updatedMissions.splice(startIndex, 1)
    updatedMissions.splice(dropIndex, 0, movedMission)
    setMissions(updatedMissions)

    const nextSelected = new Set<number>()
    let wasSelected = selectedMissionIndices.has(startIndex)

    // Simplistic remapping for checkbox states across a drag
    selectedMissionIndices.forEach(i => {
      if (i === startIndex) return
      if (startIndex < dropIndex && i > startIndex && i <= dropIndex) nextSelected.add(i - 1)
      else if (startIndex > dropIndex && i >= dropIndex && i < startIndex) nextSelected.add(i + 1)
      else nextSelected.add(i)
    })
    if (wasSelected) nextSelected.add(dropIndex)

    setSelectedMissionIndices(nextSelected)

    // Remap equipment setups dictionary
    const currentSetups = scenario.mission_equipment_setups || {}
    const newSetups: Record<string, { front_lights?: boolean }> = {}
    const numMissions = updatedIds.length

    for (let i = 0; i < numMissions; i++) {
      let oldIdx = i
      if (i === dropIndex) {
        oldIdx = startIndex
      } else if (startIndex < dropIndex && i >= startIndex && i < dropIndex) {
        oldIdx = i + 1
      } else if (startIndex > dropIndex && i > dropIndex && i <= startIndex) {
        oldIdx = i - 1
      }

      if (currentSetups[oldIdx]) {
        newSetups[i] = currentSetups[oldIdx]
      }
    }

    setDraggedIndex(null)
    setDragOverIndex(null)

    try {
      await MainApi.patchScenarios(scenario.id, {
        missions_ids: updatedIds,
        mission_equipment_setups: newSetups,
      } as any)
      onUpdated(true)
    } catch (error) {
      console.error('Error dragging mission:', error)
      fetchMissions(true)
    }
  }

  return (
    <div className={`${style.container} ${pageLayoutStyle.contentTopPadding}`}>
      <div className={pageLayoutStyle.pageHeaderArea}>
        <div className={clsx(style.titleBlock, pageLayoutStyle.titleBlockMargin)}>
          <Title text={scenario.name} />
          <div className={style.maintenanceButtonsGroup}>
            <Button.Outlined
              className={clsx(style.maintenanceButton, {
                [style.maintenanceButtonActive]: scenario.maintenance?.washing,
              })}
              onClick={async () => {
                const maintenance =
                  scenario.maintenance && typeof scenario.maintenance === 'object' ? scenario.maintenance : {}
                const val = !maintenance.washing
                await MainApi.patchScenarios(scenario.id, {
                  maintenance: { ...maintenance, washing: val },
                })
                onUpdated(true)
              }}
            >
              <WashingSvg width={24} height={24} />
            </Button.Outlined>
            <Button.Outlined
              className={clsx(style.maintenanceButton, {
                [style.maintenanceButtonActive]: scenario.maintenance?.charging,
              })}
              onClick={async () => {
                const maintenance =
                  scenario.maintenance && typeof scenario.maintenance === 'object' ? scenario.maintenance : {}
                const val = !maintenance.charging
                await MainApi.patchScenarios(scenario.id, {
                  maintenance: { ...maintenance, charging: val },
                })
                onUpdated(true)
              }}
            >
              <ChargingSvg width={24} height={24} />
            </Button.Outlined>
            {/* <Button.Outlined
              className={clsx(style.maintenanceButton, { [style.maintenanceButtonActive]: scenario.maintenance?.parking })}
              onClick={async () => {
                const maintenance = (scenario.maintenance && typeof scenario.maintenance === 'object') ? scenario.maintenance : {}
                const val = !maintenance.parking
                await MainApi.patchScenarios(scenario.id, {
                  maintenance: { ...maintenance, parking: val }
                })
                onUpdated(true)
              }}
            >
              <ParkingSvg width={24} height={24} />
            </Button.Outlined> */}
            <Button.Standart style={{ width: '250px' }} onClick={() => setAddMissionDialog(true)}>
              Add a Mission
            </Button.Standart>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start' }}>
          <RobotTypeLabel
            vendor={VENDOR.AUTONOMICS}
            robotType={scenario.robot_type}
            inconsistent={scenario.missions_type_inconsistent}
          />
        </div>
      </div>

      <div className={style.main}>
        <Box className={style.box}>
          <MissionStats
            coverage={scenario.overall_cleaned_area || 0}
            length={scenario.overall_length || 0}
            time={scenario.overall_execution_time || 0}
          />

          {missionsProgress === PROGRESS_TYPES.WORK && <Loader />}
          {missionsProgress === PROGRESS_TYPES.ERROR && (
            <div className={style.emptyWrapper}>
              <div className={style.emptyTitle}>Error loading missions</div>
            </div>
          )}
          {missionsProgress === PROGRESS_TYPES.SUCCESS && (
            <>
              {/* Table header */}
              <div className={style.missionsTableHeader}>
                <div className={style.headerColCentered}></div>
                <div className={clsx(style.headerCol, 'text-16-400')}>{t('missions.id')}</div>
                <div className={clsx(style.headerCol, 'text-16-400')}>{t('missions.name')}</div>
                <div className={clsx(style.headerColCentered, 'text-16-400')}>{t('missions.localization')}</div>
                <div className={clsx(style.headerColCentered, 'text-16-400')} style={{ cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                    <input
                      type="checkbox"
                      checked={areAllLightsOn}
                      onChange={e => toggleAllEquipmentSetup(e.target.checked)}
                      onClick={e => e.stopPropagation()}
                    />
                    Lights
                  </div>
                </div>
                <div className={clsx(style.headerColCentered, 'text-16-400')}>
                  <input
                    type="checkbox"
                    checked={missions.length > 0 && selectedMissionIndices.size === missions.length}
                    onChange={toggleShowAll}
                  />
                </div>
                <div className={style.headerCol}></div>
              </div>
              {missions.length > 0 ||
              scenario.maintenance?.washing ||
              scenario.maintenance?.charging ||
              scenario.maintenance?.parking ? (
                <div className={style.table}>
                  {missions.map((mission, index) => (
                    <ScenarioItem
                      key={`${mission.id}-${index}`}
                      mission={mission}
                      onClick={() => setHighlightedMissionId(mission.id)}
                      highlighted={highlightedMissionId === mission.id}
                      onExclude={() => excludeMission(index)}
                      onMoveUp={() => moveMission(index, 'up')}
                      onMoveDown={() => moveMission(index, 'down')}
                      isFirst={index === 0}
                      isLast={index === missions.length - 1}
                      location={scenario.missions_locations?.[index]}
                      showCheckbox={true}
                      checked={selectedMissionIndices.has(index)}
                      onCheckboxChange={isChecked => {
                        const newSet = new Set(selectedMissionIndices)
                        if (isChecked) {
                          newSet.add(index)
                        } else {
                          newSet.delete(index)
                        }
                        setSelectedMissionIndices(newSet)
                      }}
                      typeMismatch={Boolean(
                        scenario.missions_type_inconsistent &&
                          scenario.robot_type != null &&
                          mission.robot_type !== scenario.robot_type
                      )}
                      equipmentSetup={scenario.mission_equipment_setups?.[index]}
                      onEquipmentSetupChange={() => toggleEquipmentSetup(index)}
                      index={index}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                      isDragging={draggedIndex === index}
                    />
                  ))}
                  {scenario.maintenance?.washing && <MaintenanceItem type="Washing" icon={WashingSvg} />}
                  {scenario.maintenance?.charging && <MaintenanceItem type="Charging" icon={ChargingSvg} />}
                  {scenario.maintenance?.parking && <MaintenanceItem type="Parking" icon={ParkingSvg} />}
                </div>
              ) : (
                <div className={style.emptyWrapper}>
                  <div className={style.emptyTitle}>No missions in this scenario</div>
                </div>
              )}
            </>
          )}
        </Box>

        {/* Map */}
        <MapboxCommon ref={mapRef} disableRuler={false} overlayItems={[]} onMapInited={onMapInited}>
          {individualMissionDetails.length > 0 && (
            <>
              <RestrictZonesOnMap restrictZones={individualMissionDetails.flatMap(m => m.restrictZones)} />
              <CleaningZonesOnMap cleaningZones={individualMissionDetails.flatMap(m => m.cleaningZones)} />
              <CleaningRoutesOnMap cleaningRoutes={individualMissionDetails.flatMap(m => m.cleaningRoutes)} />
              <TransferRoutesOnMap
                transferRoutes={individualMissionDetails.flatMap(m => [...m.transferRoutes, ...(m.pointRoutes || [])])}
              />
              <CleaningPointsOnMap
                cleaningRoutes={individualMissionDetails.flatMap(m => m.cleaningRoutes)}
                launchPoints={individualMissionDetails.flatMap(m => m.launchPoints)}
              />
              <CleaningZonesNamesOnMap cleaningZones={individualMissionDetails.flatMap(m => m.cleaningZones)} />
              <MissionStartFinishPoints
                startPoints={individualMissionDetails.flatMap(m => m.startingPoint)}
                finishPoints={individualMissionDetails.flatMap(m => m.finishingPoint)}
              />
            </>
          )}
        </MapboxCommon>
      </div>
      <SelectMissionModal
        isOpen={addMissionDialog}
        onClose={() => setAddMissionDialog(false)}
        onConfirmClick={addMission}
        robotType={scenario.robot_type && scenario.robot_type !== 'Unknown' ? scenario.robot_type : undefined}
        only_ok_missions={false}
      />
    </div>
  )
}

export default ScenarioView
